import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface OAModalData {
  visible: boolean;
  emailDetail?: any;
  visibleRouteKeys?: string[];
}

type OAModalType = 'process' | 'list' | 'saasProcess' | 'saasList';

interface OAModalsState {
  [key: string]: OAModalData;
}

interface OAProcessModalContextProps {
  modalsState: OAModalsState;
  setModalData: (modalType: OAModalType, data: Partial<OAModalData>) => void;
  openModal: (modalType: OAModalType, visibleRouteKeys?: string[]) => void;
  closeModal: (modalType: OAModalType) => void;
  updateAllModalsEmailDetail: (emailDetail: any, type: 'email' | 'saas') => void;
}

const OAProcessModalContext = createContext<OAProcessModalContextProps>({
  modalsState: {},
  setModalData: () => { },
  openModal: () => { },
  closeModal: () => { },
  updateAllModalsEmailDetail: () => { },
});

export const useOAProcessModalContext = () => {
  const context = useContext(OAProcessModalContext);
  if (!context) {
    throw new Error('useOAProcessModalContext must be used within OAProcessModalProvider');
  }
  return context;
};

interface OAProcessModalProviderProps {
  children: ReactNode;
}

export const OAProcessModalProvider: React.FC<OAProcessModalProviderProps> = ({ children }) => {
  const [modalsState, setModalsState] = useState<OAModalsState>({
    process: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    list: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    saasProcess: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
    saasList: {
      visible: false,
      emailDetail: undefined,
      visibleRouteKeys: undefined,
    },
  });

  const setModalData = useCallback((modalType: OAModalType, data: Partial<OAModalData>) => {
    setModalsState((prev) => ({
      ...prev,
      [modalType]: {
        ...prev[modalType],
        ...data,
      },
    }));
  }, []);

  const openModal = useCallback((modalType: OAModalType, visibleRouteKeys?: string[]) => {
    setModalsState((prev) => {
      return {
        ...prev,
        [modalType]: {
          visible: true,
          emailDetail: prev[modalType]?.emailDetail,
          visibleRouteKeys,
        },
      };
    });
  }, []);

  const closeModal = useCallback((modalType: OAModalType) => {
    setModalsState((prev) => ({
      ...prev,
      [modalType]: {
        ...prev[modalType],
        visible: false,
      },
    }));
  }, []);

  // 更新所有 modal 的 emailDetail（用于邮件详情变化时同步）
  const updateAllModalsEmailDetail = useCallback((emailDetail: any, type: 'email' | 'saas') => {
    // 邮件工单 - 更新'process' | 'list'
    // SAAS工单 - 更新'saasProcess' | 'saasList'
    const modalTypes = type === 'email' ? ['process', 'list'] : ['saasProcess', 'saasList'];
    modalTypes.forEach((modalType) => {
      setModalsState((prev) => ({
        ...prev,
        [modalType]: {
          ...prev[modalType],
          emailDetail,
        },
      }));
    });
  }, []);

  return (
    <OAProcessModalContext.Provider
      value={{
        modalsState,
        setModalData,
        openModal,
        closeModal,
        updateAllModalsEmailDetail,
      }}
    >
      {children}
    </OAProcessModalContext.Provider>
  );
};

